import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import ProductCard from './ProductCard';
import ProductCardSkeleton from './ProductCardSkeleton';
import Footer from './Footer';
import ModernHeroSlider from './ModernHeroSlider';
import SEOHead from './SEOHead';
import { apiGet } from '../utils/api';
import { useDarkMode } from '../contexts/DarkModeContext';

const Home = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [seoSettings, setSeoSettings] = useState(null);
  const { isDarkMode } = useDarkMode();

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const res = await apiGet('/api/products/boosted-random');
        const data = await res.json();
        setProducts(Array.isArray(data) ? data.filter(p => p.status === 'published') : []);
      } catch (err) {
        // Error fetching products (silently ignored in production)
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadProducts();
  }, []);

  // Load SEO settings
  useEffect(() => {
    const loadSeoSettings = async () => {
      try {
        const res = await apiGet('/api/page-seo/page/');
        const data = await res.json();
        if (data && !data.error) {
          setSeoSettings(data);
        }
      } catch (error) {
        // Error loading SEO settings (silently ignored in production)
      }
    };
    loadSeoSettings();
  }, []);

  // Helper to render skeletons or products
  const renderSection = (filteredProducts, count = 8) =>
    isLoading
      ? Array.from({ length: count }).map((_, i) => <ProductCardSkeleton key={i} />)
      : filteredProducts.slice(0, count).map((product, idx) => product && <ProductCard key={product._id || idx} product={product} />);

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${
      isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'
    }`}>
      <SEOHead 
        title={seoSettings?.siteTitle || "YTProud - Social Media Account Marketplace"}
        description={seoSettings?.siteDescription || "Buy and sell verified social media accounts including YouTube, TikTok, Instagram, and Facebook pages. Secure marketplace for social media accounts."}
        keywords={seoSettings?.siteKeywords || "youtube channels, tiktok accounts, instagram accounts, facebook pages, social media marketplace, buy social media accounts"}
        ogTitle={seoSettings?.ogTitle || "YTProud - Social Media Account Marketplace"}
        ogDescription={seoSettings?.ogDescription || "Buy and sell verified social media accounts securely"}
        ogImage={seoSettings?.ogImage || "/images/ytproud.png"}
        schemaEnabled={seoSettings?.schemaEnabled || false}
      />
      <Header />
      {/* Modern Hero Section with Slider */}
      <div className={`py-4 sm:py-6 lg:py-8 transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-[#0f0f0f]' 
          : 'bg-gradient-to-br from-blue-50 to-white'
      }`}>
        <div className="max-w-[1525px] mx-auto px-4 sm:px-5">
          {/* Simple custom slider */}
          <ModernHeroSlider />
          {/* Feature Cards Row */}
          <div className="mt-6 sm:mt-8 lg:mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 max-w-[1495px] mx-auto">
            <div className="rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 flex flex-col items-center text-center hover:shadow-xl sm:hover:shadow-2xl hover:bg-gray-50 dark:hover:bg-[#4a4a4a] transition transition-colors duration-300 bg-white dark:bg-[#282828]">
              <span className="bg-red-100 dark:bg-red-900/30 p-2 sm:p-3 rounded-full mb-2 sm:mb-3">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              </span>
              <h3 className="text-base sm:text-lg font-extrabold mb-1 sm:mb-2 transition-colors duration-300 text-gray-900 dark:text-white">Secure Transactions</h3>
              <p className="text-xs sm:text-sm leading-relaxed transition-colors duration-300 text-gray-500 dark:text-gray-300">Safe and secure platform for buying and selling social media accounts</p>
            </div>
            <div className="rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 flex flex-col items-center text-center hover:shadow-xl sm:hover:shadow-2xl hover:bg-gray-50 dark:hover:bg-[#4a4a4a] transition transition-colors duration-300 bg-white dark:bg-[#282828]">
              <span className="bg-blue-100 dark:bg-blue-900/30 p-2 sm:p-3 rounded-full mb-2 sm:mb-3">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" /></svg>
              </span>
              <h3 className="text-base sm:text-lg font-extrabold mb-1 sm:mb-2 transition-colors duration-300 text-gray-900 dark:text-white">24/7 Support</h3>
              <p className="text-xs sm:text-sm leading-relaxed transition-colors duration-300 text-gray-500 dark:text-gray-300">Round-the-clock customer support to assist you with any queries</p>
            </div>
            <div className="rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 flex flex-col items-center text-center hover:shadow-xl sm:hover:shadow-2xl hover:bg-gray-50 dark:hover:bg-[#4a4a4a] transition transition-colors duration-300 bg-white dark:bg-[#282828]">
              <span className="bg-green-100 dark:bg-green-900/30 p-2 sm:p-3 rounded-full mb-2 sm:mb-3">
                <svg width="20" height="20" className="sm:w-6 sm:h-6" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="16" cy="16" r="16" fill="#D1FAE5" className="dark:fill-green-900/30"/>
                  <g>
                    {/* Shield with checkmark */}
                    <circle cx="16" cy="16" r="12" fill="#ECFDF5" stroke="#34D399" strokeWidth="2"/>
                    <path d="M16 11L21 14V18C21 21 16 22.5 16 22.5C16 22.5 11 21 11 18V14L16 11Z" fill="#34D399" stroke="#059669" strokeWidth="1.2"/>
                    <path d="M14.5 17L16 18.5L18.5 15.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    {/* Lightning bolt */}
                    <polygon points="23,22 25,24 23.8,24 25,27 22,24.5 23.2,24.5" fill="#FBBF24" stroke="#F59E42" strokeWidth="0.5"/>
                  </g>
                </svg>
              </span>
              <h3 className="text-base sm:text-lg font-extrabold mb-1 sm:mb-2 transition-colors duration-300 text-gray-900 dark:text-white">Easy Account Transfers</h3>
              <p className="text-xs sm:text-sm leading-relaxed transition-colors duration-300 text-gray-500 dark:text-gray-300">Fast, simple, and secure transfer process for all accounts</p>
            </div>
            <div className="rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 flex flex-col items-center text-center hover:shadow-xl sm:hover:shadow-2xl hover:bg-gray-50 dark:hover:bg-[#4a4a4a] transition transition-colors duration-300 bg-white dark:bg-[#282828]">
              <span className="bg-yellow-100 dark:bg-yellow-900/30 p-2 sm:p-3 rounded-full mb-2 sm:mb-3">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500 dark:text-yellow-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
              </span>
              <h3 className="text-base sm:text-lg font-extrabold mb-1 sm:mb-2 transition-colors duration-300 text-gray-900 dark:text-white">Trusted by Thousands</h3>
              <p className="text-xs sm:text-sm leading-relaxed transition-colors duration-300 text-gray-500 dark:text-gray-300">Thousands of satisfied customers worldwide.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-5 py-4 sm:py-6 lg:py-8 flex-1 w-full" style={{ maxWidth: '1525px' }}>
        {/* Featured Accounts for Sale Section */}
        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6 w-full max-w-[1495px] mx-auto">
          <h2 className="text-2xl sm:text-3xl font-extrabold whitespace-nowrap mr-3 sm:mr-4 transition-colors duration-300 text-gray-900 dark:text-white">Featured Accounts for Sale</h2>
          <div className="flex-1 h-1 bg-gradient-to-r from-red-600 via-red-400 to-transparent rounded-full" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 lg:gap-6 max-w-[1495px] mx-auto">
          {renderSection(Array.isArray(products) ? products.filter(Boolean) : [])}
        </div>
        <div className="flex justify-end mt-4 sm:mt-6 max-w-[1495px] mx-auto">
          <a
            href="/marketplace"
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-2.5 sm:py-3 rounded-full bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold shadow-lg shadow-red-600/25 hover:from-red-700 hover:to-red-800 transform hover:scale-105 transition-all duration-300 text-base sm:text-lg"
          >
            Marketplace
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        {/* YouTube Section */}
        <div className="flex items-center gap-3 sm:gap-4 mt-8 sm:mt-12 mb-4 sm:mb-6 w-full max-w-[1495px] mx-auto">
          <h2 className="text-2xl sm:text-3xl font-extrabold whitespace-nowrap mr-3 sm:mr-4 transition-colors duration-300 text-gray-900 dark:text-white">YouTube</h2>
          <div className="flex-1 h-1 bg-gradient-to-r from-red-600 via-red-400 to-transparent rounded-full" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 max-w-[1495px] mx-auto gap-2 sm:gap-3 lg:gap-6">
          {renderSection(Array.isArray(products) ? products.filter(p => (p.accountType === 'youtube' || p.platform === 'youtube')) : [])}
        </div>
        <div className="flex justify-end mt-4 sm:mt-6 mb-2 max-w-[1495px] mx-auto">
          <a
            href="/buy-youtube-channel"
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-2.5 sm:py-3 rounded-full bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold shadow-lg shadow-red-600/25 hover:from-red-700 hover:to-red-800 transform hover:scale-105 transition-all duration-300 text-base sm:text-lg"
          >
            All YouTube Channels
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        {/* Tiktok Section */}
        <div className="flex items-center gap-3 sm:gap-4 mt-8 sm:mt-12 mb-4 sm:mb-6 w-full max-w-[1495px] mx-auto">
          <h2 className="text-2xl sm:text-3xl font-extrabold whitespace-nowrap mr-3 sm:mr-4 transition-colors duration-300 text-gray-900 dark:text-white">Tiktok</h2>
          <div className="flex-1 h-1 bg-gradient-to-r from-red-600 via-red-400 to-transparent rounded-full" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 max-w-[1495px] mx-auto gap-2 sm:gap-3 lg:gap-6">
          {renderSection(Array.isArray(products) ? products.filter(p => (p.accountType === 'tiktok' || p.platform === 'tiktok')) : [])}
        </div>
        <div className="flex justify-end mt-4 sm:mt-6 mb-2 max-w-[1495px] mx-auto">
          <a
            href="/buy-tiktok-account"
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-2.5 sm:py-3 rounded-full bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold shadow-lg shadow-red-600/25 hover:from-red-700 hover:to-red-800 transform hover:scale-105 transition-all duration-300 text-base sm:text-lg"
          >
            All Tiktok Accounts
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        {/* Facebook Section */}
        <div className="flex items-center gap-3 sm:gap-4 mt-8 sm:mt-12 mb-4 sm:mb-6 w-full max-w-[1495px] mx-auto">
          <h2 className="text-2xl sm:text-3xl font-extrabold whitespace-nowrap mr-3 sm:mr-4 transition-colors duration-300 text-gray-900 dark:text-white">Facebook</h2>
          <div className="flex-1 h-1 bg-gradient-to-r from-red-600 via-red-400 to-transparent rounded-full" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 max-w-[1495px] mx-auto gap-2 sm:gap-3 lg:gap-6">
          {renderSection(Array.isArray(products) ? products.filter(p => (p.accountType === 'facebook' || p.platform === 'facebook')) : [])}
        </div>
        <div className="flex justify-end mt-4 sm:mt-6 mb-2 max-w-[1495px] mx-auto">
          <a
            href="/buy-facebook-page"
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-2.5 sm:py-3 rounded-full bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold shadow-lg shadow-red-600/25 hover:from-red-700 hover:to-red-800 transform hover:scale-105 transition-all duration-300 text-base sm:text-lg"
          >
            All Facebook Pages
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        {/* Instagram Section */}
        <div className="flex items-center gap-3 sm:gap-4 mt-8 sm:mt-12 mb-4 sm:mb-6 w-full max-w-[1495px] mx-auto">
          <h2 className="text-2xl sm:text-3xl font-extrabold whitespace-nowrap mr-3 sm:mr-4 transition-colors duration-300 text-gray-900 dark:text-white">Instagram</h2>
          <div className="flex-1 h-1 bg-gradient-to-r from-red-600 via-red-400 to-transparent rounded-full" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 max-w-[1495px] mx-auto gap-2 sm:gap-3 lg:gap-6">
          {renderSection(Array.isArray(products) ? products.filter(p => (p.accountType === 'instagram' || p.platform === 'instagram')) : [])}
        </div>
        <div className="flex justify-end mt-4 sm:mt-6 mb-2 max-w-[1495px] mx-auto">
          <a
            href="/buy-instagram-account"
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-2.5 sm:py-3 rounded-full bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold shadow-lg shadow-red-600/25 hover:from-red-700 hover:to-red-800 transform hover:scale-105 transition-all duration-300 text-base sm:text-lg"
          >
            All Instagram Accounts
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
      {/* Modern About/Info Box above Footer */}
      <div className={`w-full flex justify-center py-4 sm:py-6 transition-colors duration-300 ${
        isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'
      }`}>
        <div className={`relative rounded-2xl sm:rounded-3xl shadow-lg px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16 w-full mx-4 flex flex-col items-center justify-center text-center border transition-colors duration-300 ${
          isDarkMode 
            ? 'bg-[#3f3f3f] border-gray-700' 
            : 'bg-white border-red-100'
        }`} style={{ maxWidth: '1650px', boxShadow: isDarkMode ? '0 4px 32px 0 rgba(0,0,0,0.3)' : '0 4px 32px 0 #fee2e2' }}>
          {/* Animated, blurred social icons inside the box */}
          <div className="absolute inset-0 pointer-events-none z-0">
            {/* Top left */}
            <img src="/images/categories/youtube.svg" alt="YouTube"
              className="blur-2xl opacity-30 w-20 h-20 sm:w-32 sm:h-32 absolute top-4 sm:top-6 left-6 sm:left-10 animate-floatY"
              style={{ filter: 'blur(32px)' }} />
            {/* Top right */}
            <img src="/images/categories/facebook.svg" alt="Facebook"
              className="blur-2xl opacity-20 w-16 h-16 sm:w-28 sm:h-28 absolute top-8 sm:top-12 right-10 sm:right-16 animate-floatX"
              style={{ filter: 'blur(24px)' }} />
            {/* Bottom left */}
            <img src="/images/categories/instagram.svg" alt="Instagram"
              className="blur-2xl opacity-20 w-24 h-24 sm:w-36 sm:h-36 absolute bottom-6 sm:bottom-10 left-12 sm:left-20 animate-floatY2"
              style={{ filter: 'blur(24px)' }} />
            {/* Bottom right */}
            <img src="/images/categories/tiktok.svg" alt="TikTok"
              className="blur-2xl opacity-20 w-20 h-20 sm:w-32 sm:h-32 absolute bottom-10 sm:bottom-16 right-16 sm:right-24 animate-floatX2"
              style={{ filter: 'blur(24px)' }} />
            {/* Center left */}
            <img src="/images/categories/youtube.svg" alt="YouTube"
              className="blur-2xl opacity-10 w-24 h-24 sm:w-40 sm:h-40 absolute top-1/2 left-0 -translate-y-1/2 animate-floatY"
              style={{ filter: 'blur(32px)' }} />
            {/* Center right */}
            <img src="/images/categories/facebook.svg" alt="Facebook"
              className="blur-2xl opacity-10 w-24 h-24 sm:w-40 sm:h-40 absolute top-1/2 right-0 -translate-y-1/2 animate-floatX"
              style={{ filter: 'blur(32px)' }} />
            {/* Center (very faint) */}
            <img src="/images/categories/instagram.svg" alt="Instagram"
              className="blur-2xl opacity-5 w-48 h-48 sm:w-80 sm:h-80 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-floatY2"
              style={{ filter: 'blur(48px)' }} />
          </div>
          {/* Hero content (text and button) */}
          <div className="relative z-10 w-full flex flex-col items-center justify-center text-center">
            <h2 className={`uppercase text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold mb-4 sm:mb-6 leading-tight tracking-tight transition-colors duration-300 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              YTproud: The Trusted Marketplace<br className="hidden sm:block" />to Buy & Sell Social Media Accounts!
            </h2>
            <p className={`text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 max-w-2xl mx-auto transition-colors duration-300 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Buy and sell top-quality social media accounts with confidence.<br className="hidden sm:block" />
              Seamless transactions and 24/7 support for your growth.
            </p>
                          <a
                href="/marketplace"
                className="inline-flex items-center gap-2 px-8 sm:px-10 lg:px-12 py-3 sm:py-4 lg:py-5 rounded-full bg-gradient-to-r from-red-600 to-red-700 text-white font-bold shadow-lg shadow-red-600/25 hover:from-red-700 hover:to-red-800 transform hover:scale-105 transition-all duration-300 text-base sm:text-lg lg:text-xl mt-2"
              >
              Marketplace
              <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Home; 